/**
 * Pure helpers for the shelf-count screen (`/count`) — grouping, shelf ordering, progress, the
 * implausible-entry guard, and (pulled-blanks v2, B2) the shelf-basis arithmetic: what a row displays
 * as pulled, what it posts, how a refusal reads, and the hub's three pull items. No React and no IO, so
 * the money-adjacent rules are unit-testable on their own.
 *
 * ⚠️ THE ONE RULE THIS FILE EXISTS TO ENFORCE: a cached number is fine for a WARNING, never for a
 * WRITE. `CountRow.book` is on-hand as it read when the sheet loaded; the server derives the delta from
 * its OWN read at write time, so posting a client-cached figure would resurrect anything consumed while
 * the operator was walking the shelf. `book` therefore feeds the variance display and
 * {@link isImplausible} only — the number posted is always the one a human typed after looking at the
 * shelf. The pulled stacks a row shows ride along as ADVISORY ids (`pulledSeen`: sheet, record revision,
 * the quantity shown) so the server can tell "the press drew it" from "the record changed" — never as a
 * quantity the server adds.
 */
import type {
  BlankCountShelfRequest,
  BlankInventoryDetail,
  BlankOnHand,
  BlankPulledSeen,
  BlankPulledSheet,
  BlankStillOut,
  BlankUnconfirmedSheet,
} from '@fsp/contracts';
import {
  type BlankName,
  blankLabel,
  isRealSize,
  normalizeSizeName,
  sizeRank,
} from './inventory-lib';

/** How the page counts: `shelf` on the count-sheet read; `legacy` on an old server or a degraded read
 *  (the server could not derive the pulled stacks) — then the figure typed IS the book, as before B2. */
export type CountMode = 'shelf' | 'legacy';

/** One blank's pulled block as the count row carries it (a `CountSheetRow`'s pulled fields). */
export type PulledRow = {
  /** Σ `out` over `sheets` — what the server adds to the shelf figure. */
  now: number;
  sheets: readonly BlankPulledSheet[];
  unconfirmed: readonly BlankUnconfirmedSheet[];
  stillOut: readonly BlankStillOut[];
  /** The block's own `asOf` after a per-row reload (`GET /inventory/:ssSku`); else the sheet's. */
  asOf?: string;
};

/** The shape the count screen needs — a count-sheet row, or (old server) a `GET /inventory` item. */
export type CountSource = BlankName & {
  book: number;
  lastCountedAt?: string;
  lastCountedBasis?: 'book' | 'shelf';
  archived?: boolean;
  demandOnly?: boolean;
  /** Absent on an old server or a degraded read — the row then counts the legacy way. */
  pulledQty?: number;
  pulledSheets?: readonly BlankPulledSheet[];
  unconfirmed?: readonly BlankUnconfirmedSheet[];
  stillOut?: readonly BlankStillOut[];
};

/** A `GET /inventory` item as a count source — the old server's read (no pulled block). */
export function legacySource(item: BlankOnHand): CountSource {
  return {
    ssSku: item.ssSku,
    brandName: item.brandName,
    styleName: item.styleName,
    colorName: item.colorName,
    sizeName: item.sizeName,
    book: item.onHand,
    lastCountedAt: item.lastCountedAt,
    lastCountedBasis: item.lastCountedBasis,
    archived: item.archived,
  };
}

export type CountRow = {
  ssSku: string;
  /**
   * Big left-hand token — the canonical size, and ONLY when it's a real apparel size. A cap's size
   * ("M/L", "Adjustable") isn't a discriminator between its colourways, so those rows carry no badge
   * and lean on `variant` instead.
   */
  badge?: string;
  /** "Tultex 602" */
  title: string;
  /** "Black · L" */
  variant: string;
  /** On-hand when the sheet loaded. DISPLAY + {@link isImplausible} ONLY — never posted. */
  book: number;
  lastCountedAt?: string;
  lastCountedBasis?: 'book' | 'shelf';
  /** The pulled block; `undefined` = the server could not say (legacy / degraded): the row counts the
   *  old way, the figure typed is the book. */
  pulled?: PulledRow;
};

export type CountGroup = {
  /** Stable identity for React keys + the sticky header. */
  key: string;
  /** "Tultex 602" */
  label: string;
  rows: CountRow[];
};

/**
 * Build the shelf walk: one group per brand+style, sizes in wearing order inside it.
 *
 * Grouping is by brand+STYLE, not brand+style+colour, deliberately — the shop stocks one colourway of
 * each tee (so colour adds nothing) while the caps are one size across many colourways (so splitting
 * on colour would shatter 11 cap rows into 11 one-row groups). Archived blanks are dropped: they're
 * retired from the working views and would be permanently-skipped rows on every walk.
 *
 * Order is deliberate and NOT the server's. `GET /inventory` sorts by `ssSku`, which puts 5XL at the
 * top of both tee families (`B13659500` is the 5XL) — an invitation to key a size's count into the
 * row above it. Groups run widest-first so the main shelf run comes before the one-off cap pegs.
 */
export function buildCountSheet(items: readonly CountSource[]): CountGroup[] {
  const groups = new Map<string, CountGroup>();

  for (const it of items) {
    if (it.archived) continue;
    const title = blankLabel(it);
    const key = title.toLowerCase();
    const size = normalizeSizeName(it.sizeName ?? '');
    const colour = it.colorName ?? '';

    const row: CountRow = {
      ssSku: it.ssSku,
      badge: isRealSize(size) ? size : undefined,
      title,
      variant: [colour, it.sizeName].filter(Boolean).join(' · ') || it.ssSku,
      book: it.book,
      lastCountedAt: it.lastCountedAt,
      lastCountedBasis: it.lastCountedBasis,
      // `pulledQty` is set (even as 0) whenever the server derived the block; its absence IS the
      // degraded / old-server signal, so the row must not invent a block from empty arrays.
      pulled:
        it.pulledQty === undefined
          ? undefined
          : {
              now: it.pulledQty,
              sheets: it.pulledSheets ?? [],
              unconfirmed: it.unconfirmed ?? [],
              stillOut: it.stillOut ?? [],
            },
    };

    const g = groups.get(key);
    if (g) g.rows.push(row);
    else groups.set(key, { key, label: title, rows: [row] });
  }

  for (const g of groups.values()) {
    g.rows.sort(
      (a, b) =>
        sizeRank(a.badge ?? '') - sizeRank(b.badge ?? '') ||
        a.variant.localeCompare(b.variant) ||
        a.ssSku.localeCompare(b.ssSku),
    );
  }

  return [...groups.values()].sort(
    (a, b) => b.rows.length - a.rows.length || a.label.localeCompare(b.label),
  );
}

/** Every row of the sheet, in walk order — the sequence "jump to the next uncounted bin" follows. */
export function flattenSheet(groups: readonly CountGroup[]): CountRow[] {
  return groups.flatMap((g) => g.rows);
}

/** Strict non-negative integer from a raw input, or null. Empty, signs, decimals and junk are null. */
export function parseCount(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d{1,4}$/.test(t)) return null;
  return Number.parseInt(t, 10);
}

/**
 * Input-sanity thresholds for {@link isImplausible}. Deliberately NOT a tolerance band — there is no
 * auto-post/escalate policy here, no per-class configuration and no accuracy program. This catches one
 * thing: a typo big enough to matter (a dropped or doubled digit), with a single inline confirm.
 */
export const IMPLAUSIBLE_MIN_DELTA = 25;
export const IMPLAUSIBLE_RATIO = 5;
export const IMPLAUSIBLE_MAX = 500;
/** Emptying a bin the book thinks is well stocked always confirms — see {@link isImplausible}. */
export const ZERO_CONFIRM_MIN_BOOK = 10;

/**
 * Should this entry be confirmed once before it's written?
 *
 * Two independent reasons, because they're different worries:
 *
 * **A keying slip.** Both a big absolute swing (≥ 25 units, against a whole shelf of ~250 and a
 * biggest bin of ~32) AND a fivefold jump one way or the other — that pairing is the shape `14 → 144`
 * makes and the shape restocking does not. Requiring both keeps the guard silent during ordinary
 * counting, which is what stops people learning to tap through it. An absurd figure trips regardless.
 *
 * **Emptying a stocked bin.** Counting a bin with ≥ 10 on the book down to zero is worth one
 * deliberate tap on its own, whatever the ratio says — an empty bin is the most informative row on a
 * count sheet and the most expensive one to get wrong (it reads straight through to the reorder
 * position on the fastest-moving blanks). Without this rule the arithmetic just misses it: zeroing a
 * bin of 24 is a delta of 24, one unit under the slip threshold. Historically every zero this shop has
 * recorded was on a bin of 3 or fewer, so the floor is set above routine and below the movers.
 */
export function isImplausible(counted: number, book: number): boolean {
  if (!Number.isFinite(counted) || counted < 0) return false;
  if (counted > IMPLAUSIBLE_MAX) return true;
  if (counted === 0 && book >= ZERO_CONFIRM_MIN_BOOK) return true;
  if (Math.abs(counted - book) < IMPLAUSIBLE_MIN_DELTA) return false;
  return counted >= book * IMPLAUSIBLE_RATIO || book >= counted * IMPLAUSIBLE_RATIO;
}

/**
 * Which confirm, if any, a typed shelf figure needs before it is written.
 *  - `slip` / `zero` — {@link isImplausible} on the BOOK the count would anchor (shelf + pulled; on a
 *    legacy row the figure itself), `zero` when that book is 0 on a stocked bin.
 *  - `whole-book` — nothing on the shelf while the book would rest entirely on pull records (§2.10: the
 *    one double-count the design cannot rule out is a confirm made while the stack was still on the
 *    shelf; it is caught here when it is a bin's whole stock).
 */
export type ConfirmKind = 'slip' | 'zero' | 'whole-book';
export function confirmKind(shelfQty: number, row: CountRow): ConfirmKind | null {
  const now = row.pulled?.now ?? 0;
  const book = shelfQty + now;
  if (row.pulled && shelfQty === 0 && now > 0) return 'whole-book';
  if (!isImplausible(book, row.book)) return null;
  return book === 0 ? 'zero' : 'slip';
}

/** No sheet holds any of this blank — the off-system-job door's condition (D12 / v1 Q13): nothing recorded
 *  and still out (a fully drawn record, open or closed, holds nothing — a recent sheet that ran this blank
 *  is listed with `out: 0` and must not hide the door), no question, no leftover. Unknowable on a legacy
 *  row, so false there. */
export function noSheetHolds(row: CountRow): boolean {
  const p = row.pulled;
  return (
    !!p &&
    p.sheets.every((s) => s.out === 0) &&
    p.unconfirmed.length === 0 &&
    p.stillOut.length === 0
  );
}

export function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

/** Was this bin's last COUNT taken on `now`'s calendar day? */
export function countedToday(iso: string | undefined, now: Date): boolean {
  if (!iso) return false;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return false;
  return then.toDateString() === now.toDateString();
}

/** Done before this session started: counted today — and, when the page counts on the shelf basis, only
 *  a SHELF count counts (a book count made today still folded the pulled stacks in by eye). */
export function doneBefore(row: CountRow, now: Date, mode: CountMode): boolean {
  if (!countedToday(row.lastCountedAt, now)) return false;
  return mode === 'legacy' || row.lastCountedBasis === 'shelf';
}

export type CountProgress = {
  /** Bins covered today — saved in this session, OR already carrying a count from earlier today. */
  done: number;
  /** Bins on the sheet. */
  total: number;
  /** Bins saved in THIS session whose count differed from the book figure the sheet loaded with. */
  mismatched: number;
  /** Of `done`, the ones that were already counted before this session started. */
  carriedIn: number;
};

/**
 * Completeness for today's walk.
 *
 * A bin counts as done when this session saved it OR its `lastCountedAt` is already today (on the
 * shelf basis, when the page counts that way) — so an interrupted walk resumes correctly after a
 * reload, a browser restart, or a hand-off to a second person on another device. The durable progress
 * marker is the ledger's own `lastCountedAt`, not anything this screen keeps, which is why nothing has
 * to be stashed client-side to survive a lock screen. `mismatched` stays scoped to this session because
 * a bin counted earlier by someone else has no variance this screen can honestly claim to know.
 */
export function progressOf(
  groups: readonly CountGroup[],
  saved: ReadonlyMap<string, number>,
  now: Date,
  mode: CountMode,
): CountProgress {
  const rows = flattenSheet(groups);
  let done = 0;
  let mismatched = 0;
  let carriedIn = 0;
  for (const r of rows) {
    const n = saved.get(r.ssSku);
    if (n !== undefined) {
      done++;
      if (n !== r.book) mismatched++;
      continue;
    }
    if (doneBefore(r, now, mode)) {
      done++;
      carriedIn++;
    }
  }
  return { done, total: rows.length, mismatched, carriedIn };
}

/**
 * The next bin after `after` still needing a count — where Enter sends the cursor on a desktop walk.
 * Skips bins already counted today (by this session or an earlier one) so a resumed walk doesn't send
 * the operator back to shelves someone has already done. Returns undefined at the end of the sheet.
 */
export function nextUncounted(
  groups: readonly CountGroup[],
  saved: ReadonlyMap<string, number>,
  after: string,
  now: Date,
  mode: CountMode,
): CountRow | undefined {
  const rows = flattenSheet(groups);
  const i = rows.findIndex((r) => r.ssSku === after);
  if (i === -1) return undefined;
  for (const r of rows.slice(i + 1)) {
    if (!saved.has(r.ssSku) && !doneBefore(r, now, mode)) return r;
  }
  return undefined;
}

/** "counted 24 on Jul 31" / "never counted" — the row's provenance line. */
export function lastCountedLabel(iso: string | undefined, now: Date): string {
  if (!iso) return 'never counted';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'never counted';
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  const when = then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (days <= 0) return 'counted today';
  if (days === 1) return 'counted yesterday';
  return `counted ${when} · ${days}d ago`;
}

// ── Pulled blanks (B2) — what a row shows, what it posts, how a refusal reads ──────────────────────

/** "#219", or the first eight characters of a pre-counter sheet's id. */
export function sheetTag(s: { seq?: number; batchId: string }): string {
  return s.seq != null ? `#${s.seq}` : s.batchId.slice(0, 8);
}

/** "51 pulled (#219 24 · #220 20 · #221 7)" — only sheets with something still out are named. */
export function pulledSummary(sheets: readonly BlankPulledSheet[]): string {
  const out = sheets.filter((s) => s.out > 0);
  const now = out.reduce((n, s) => n + s.out, 0);
  if (now === 0) return 'nothing pulled';
  return `${now} pulled (${out.map((s) => `${sheetTag(s)} ${s.out}`).join(' · ')})`;
}

/** What the shelf should hold if the book and the records are right — never below 0. */
export function expectedShelf(book: number, pulledNow: number): number {
  return Math.max(0, book - pulledNow);
}

/** A recorded sheet whose row is SHORT of the sheet's listed quantity — known only when the server read
 *  the sheet's items (`onSheet`); an open sheet only (a closed sheet's leftover is *still out*). */
export function shortSheets(
  sheets: readonly BlankPulledSheet[],
): { sheet: BlankPulledSheet; short: number }[] {
  return sheets
    .filter((s) => s.status !== 'PRINTED' && s.onSheet != null && s.recorded < s.onSheet)
    .map((s) => ({ sheet: s, short: (s.onSheet ?? 0) - s.recorded }));
}

/** The advisory ids a shelf count rides with: every recorded sheet the row displayed — its id, the
 *  record revision and the `out` shown. The server compares each against the live record (moved →
 *  refused, pressed → reported). Capped at the schema's 50, largest first. */
export function pulledSeenOf(sheets: readonly BlankPulledSheet[]): BlankPulledSeen[] {
  return [...sheets]
    .sort((a, b) => b.out - a.out)
    .slice(0, 50)
    .map((s) => ({ batchId: s.batchId, rev: s.rev, qty: s.out }));
}

/** The counter's answers to `pulled_rose` refusals, accumulated across resubmits of one figure. */
export type RoseAnswers = {
  exclude: readonly string[];
  confirm: readonly { batchId: string; qty: number }[];
};
export const NO_ROSE_ANSWERS: RoseAnswers = { exclude: [], confirm: [] };

/** One answer per sheet: the sheet leaves whichever list it was on and joins the one chosen — so a
 *  second tap never duplicates an id, and "Yes" then "No" ends as "No" (never in both lists, where the
 *  server's exclude would silently win). */
export function withRoseAnswer(
  prev: RoseAnswers,
  sheetId: string,
  out: number,
  kind: 'counted' | 'press',
): RoseAnswers {
  const exclude = prev.exclude.filter((id) => id !== sheetId);
  const confirm = prev.confirm.filter((c) => c.batchId !== sheetId);
  return kind === 'counted'
    ? { exclude: [...exclude, sheetId], confirm }
    : { exclude, confirm: [...confirm, { batchId: sheetId, qty: out }] };
}

/**
 * The shelf body — ONLY the typed figure plus advisory ids and answers. `asOf` and `pulledSeen` are
 * what the row loaded with; neither enters the idempotency key (`clientToken` alone does), so a
 * resubmit with an answer replays the same observation.
 */
export function shelfBody(args: {
  shelfQty: number;
  asOf: string;
  sheets: readonly BlankPulledSheet[];
  answers?: RoseAnswers;
  clientToken: string;
  reason?: string;
}): BlankCountShelfRequest {
  const a = args.answers ?? NO_ROSE_ANSWERS;
  return {
    basis: 'shelf',
    shelfQty: args.shelfQty,
    asOf: args.asOf,
    pulledSeen: pulledSeenOf(args.sheets),
    pulledExclude: a.exclude.length > 0 ? [...a.exclude] : undefined,
    confirmPulled: a.confirm.length > 0 ? a.confirm.map((c) => ({ ...c })) : undefined,
    reason: args.reason,
    clientToken: args.clientToken,
  };
}

/** The counter's answer to an unconfirmed sheet on a bin: ticked at the press (one row, via `count`),
 *  or dismissed for the walk (its stack is shelf). */
export type PullAnswer = 'press' | 'shelf';

/** The unconfirmed sheets on a row still without an answer — Save waits for every one of them. */
export function unansweredSheets(
  row: CountRow,
  answers: ReadonlyMap<string, PullAnswer> | undefined,
): BlankUnconfirmedSheet[] {
  return (row.pulled?.unconfirmed ?? []).filter((u) => !answers?.has(u.batchId));
}

/** "96 h ago" / "20 min ago" / "4 d ago" from the server's hour count. */
export function hoursLabel(ageH: number): string {
  if (!Number.isFinite(ageH) || ageH <= 0) return 'just now';
  if (ageH < 1) return `${Math.max(1, Math.round(ageH * 60))} min ago`;
  if (ageH < 48) return `${Math.round(ageH)} h ago`;
  return `${Math.round(ageH / 24)} d ago`;
}

/** The evidence an unconfirmed sheet's question names (§5): which door started the pull and when, how
 *  many of this size the sheet lists, how far the press has got. The strongest stamp is named — a print
 *  over the station's open. */
export function unconfirmedLine(u: BlankUnconfirmedSheet): string {
  const prints = [
    u.printed.pullList ? 'pull list' : null,
    u.printed.labels ? 'labels' : null,
  ].filter((x): x is string => x !== null);
  const started = prints.length > 0 ? `${prints.join(' + ')} printed` : 'pull station opened';
  return `${sheetTag(u)} · ${started} ${hoursLabel(u.ageH)} · ${u.qtyOnSheet} of this size · ${u.pressed} of ${u.total} pressed · not confirmed`;
}

/**
 * The row after a reload of its own blank (`GET /inventory/:ssSku`): the book, the last count and the
 * pulled block with ITS OWN `asOf` — what a refused save needs to be retaken without reloading the whole
 * sheet (the walk is positional; the other rows must not move). `null` when the read carried no block
 * (degraded): the row cannot be retaken on the shelf basis and says so.
 */
export function rowFromDetail(
  row: CountRow,
  detail: Pick<BlankInventoryDetail, 'onHand' | 'pulled'>,
): CountRow | null {
  if (!detail.pulled) return null;
  return {
    ...row,
    book: detail.onHand.onHand,
    lastCountedAt: detail.onHand.lastCountedAt,
    lastCountedBasis: detail.onHand.lastCountedBasis,
    pulled: {
      now: detail.pulled.now,
      sheets: detail.pulled.sheets,
      unconfirmed: detail.pulled.unconfirmed,
      stillOut: detail.pulled.stillOut,
      asOf: detail.pulled.asOf,
    },
  };
}

/**
 * Another row holding a sheet whose record just moved (the counter ticked ONE blank's row on it via
 * `count`, bumping `pulledRev` for the whole sheet): adopt the new revision on that row's recorded
 * entry, so its own save is not refused as `pulled_changed` for a change it already knows about. Only
 * the revision — its quantities are this blank's, untouched by another blank's tick. Returns the same
 * row object when nothing changed.
 */
export function adoptSheetRevision(row: CountRow, batchId: string, rev: number): CountRow {
  const p = row.pulled;
  if (!p?.sheets.some((s) => s.batchId === batchId && s.rev !== rev)) return row;
  return {
    ...row,
    pulled: {
      ...p,
      sheets: p.sheets.map((s) => (s.batchId === batchId ? { ...s, rev } : s)),
    },
  };
}

/** A refused shelf count, read from the server's error body (and HTTP status) into the row's words. */
export type Refusal =
  | {
      kind: 'rose';
      title: string;
      detail: string;
      sheets: { batchId: string; seq?: number; out: number; who?: string }[];
    }
  | { kind: 'changed'; title: string; detail: string; cause?: string }
  | { kind: 'moved'; title: string; detail: string }
  | { kind: 'moving'; title: string; detail: string }
  | { kind: 'gated'; title: string; detail: string }
  | { kind: 'stale'; title: string; detail: string }
  | { kind: 'other'; title: string; detail: string };

export function parseRefusal(
  error: { type?: unknown; title?: unknown; detail?: unknown; [k: string]: unknown } | undefined,
  status: number | undefined,
): Refusal {
  const title = typeof error?.title === 'string' ? error.title : 'Not recorded';
  const detail =
    typeof error?.detail === 'string'
      ? error.detail
      : 'The count was refused — nothing was written.';
  const type = typeof error?.type === 'string' ? error.type : '';
  if (status === 403 || type === 'forbidden') return { kind: 'gated', title, detail };
  if (type === 'pulled_rose') {
    const raw = Array.isArray(error?.sheets) ? error.sheets : [];
    const sheets = raw.flatMap((r: unknown) => {
      if (!isRecord(r)) return [];
      if (typeof r.batchId !== 'string' || typeof r.out !== 'number') return [];
      return [
        {
          batchId: r.batchId,
          seq: typeof r.seq === 'number' ? r.seq : undefined,
          out: r.out,
          who: typeof r.who === 'string' ? r.who : undefined,
        },
      ];
    });
    return { kind: 'rose', title, detail, sheets };
  }
  if (type === 'pulled_changed') {
    return {
      kind: 'changed',
      title,
      detail,
      cause: typeof error?.cause === 'string' ? error.cause : undefined,
    };
  }
  if (type === 'moved_since_load') return { kind: 'moved', title, detail };
  if (type === 'ledger_moving') return { kind: 'moving', title, detail };
  if (type === 'invalid_input' && /asOf/.test(detail)) return { kind: 'stale', title, detail };
  return { kind: 'other', title, detail };
}

// ── The strip and the hub — the sheets across every row ────────────────────────────────────────────

export type SheetStrip = {
  /** Open sheets with a record and something still out, newest `seq` first: "100 blanks on 4 sheets". */
  confirmed: { batchId: string; tag: string; out: number }[];
  blanksOut: number;
  /** Started open sheets with at least one blank unconfirmed. */
  unconfirmed: { batchId: string; tag: string; ageH: number }[];
  /** Closed sheets with blanks still out. */
  stillOut: { batchId: string; tag: string; out: number; printedAt?: string }[];
};

/** The sheets every row of the count sheet mentions, de-duplicated — the header strip and the hub hint
 *  read the same thing. */
export function sheetStrip(rows: readonly CountRow[]): SheetStrip {
  const confirmed = new Map<string, { batchId: string; tag: string; out: number; seq: number }>();
  const unconfirmed = new Map<
    string,
    { batchId: string; tag: string; ageH: number; seq: number }
  >();
  const stillOut = new Map<
    string,
    { batchId: string; tag: string; out: number; printedAt?: string; seq: number }
  >();
  for (const r of rows) {
    const p = r.pulled;
    if (!p) continue;
    for (const s of p.sheets) {
      if (s.status === 'PRINTED' || s.out <= 0) continue;
      const g = confirmed.get(s.batchId);
      if (g) g.out += s.out;
      else
        confirmed.set(s.batchId, {
          batchId: s.batchId,
          tag: sheetTag(s),
          out: s.out,
          seq: s.seq ?? 0,
        });
    }
    for (const u of p.unconfirmed) {
      const g = unconfirmed.get(u.batchId);
      if (g) g.ageH = Math.max(g.ageH, u.ageH);
      else
        unconfirmed.set(u.batchId, {
          batchId: u.batchId,
          tag: sheetTag(u),
          ageH: u.ageH,
          seq: u.seq ?? 0,
        });
    }
    for (const s of p.stillOut) {
      const g = stillOut.get(s.batchId);
      if (g) g.out += s.out;
      else
        stillOut.set(s.batchId, {
          batchId: s.batchId,
          tag: sheetTag(s),
          out: s.out,
          printedAt: s.printedAt,
          seq: s.seq ?? 0,
        });
    }
  }
  const bySeq = <T extends { seq: number; tag: string }>(a: T, b: T) =>
    a.seq - b.seq || a.tag.localeCompare(b.tag);
  // Sorted by sheet number, the sort key dropped — generic, so each list keeps its own shape.
  const strip = <T extends { seq: number; tag: string }>(m: Map<string, T>): Omit<T, 'seq'>[] =>
    [...m.values()].sort(bySeq).map(({ seq: _seq, ...rest }) => rest);
  const conf = [...confirmed.values()].sort(bySeq);
  return {
    confirmed: conf.map(({ seq: _seq, ...rest }) => rest),
    blanksOut: conf.reduce((n, s) => n + s.out, 0),
    unconfirmed: strip(unconfirmed),
    stillOut: strip(stillOut),
  };
}

/** A closed sheet's leftover is the office's business once it is this old (§2.6: "more than a few days"). */
export const STILL_OUT_ALERT_DAYS = 3;

export type HubPullItems = {
  unconfirmed: SheetStrip['unconfirmed'];
  stillOut: (SheetStrip['stillOut'][number] & { days: number })[];
  /** A short recorded at the pull with no shelf count for that blank since — "count it". */
  shorts: {
    ssSku: string;
    title: string;
    variant: string;
    short: number;
    batchId: string;
    tag: string;
  }[];
};

/**
 * The hub's three pull items (§5 Hub): the sheets whose pull started but was never confirmed, the closed
 * sheets with blanks still out for more than a few days, and the shorts recorded at the pull without
 * their shelf answer (a login without the inventory-write gate leaves one — "tell the office"). A short
 * is answered once the blank carries a SHELF count at or after the record.
 */
export function hubPullItems(rows: readonly CountRow[], nowMs: number): HubPullItems {
  const strip = sheetStrip(rows);
  const stillOut = strip.stillOut
    .map((s) => ({
      ...s,
      days: s.printedAt
        ? Math.floor((nowMs - Date.parse(s.printedAt)) / 86_400_000)
        : STILL_OUT_ALERT_DAYS,
    }))
    .filter((s) => !Number.isFinite(s.days) || s.days >= STILL_OUT_ALERT_DAYS);
  const shorts: HubPullItems['shorts'] = [];
  for (const r of rows) {
    if (!r.pulled) continue;
    for (const { sheet, short } of shortSheets(r.pulled.sheets)) {
      const answered =
        r.lastCountedBasis === 'shelf' &&
        !!r.lastCountedAt &&
        Date.parse(r.lastCountedAt) >= Date.parse(sheet.at);
      if (answered) continue;
      shorts.push({
        ssSku: r.ssSku,
        title: r.title,
        variant: r.variant,
        short,
        batchId: sheet.batchId,
        tag: sheetTag(sheet),
      });
    }
  }
  return { unconfirmed: strip.unconfirmed, stillOut, shorts };
}
